import React from 'react';
import { observer } from 'mobx-react';
import { CommonPopupBodyFooterLayout } from '../../global/common-popup/common-popup-body-footer-layout';
import { OEToggle } from '../../global/oe-toggle/oe-toggle';
import Text from '../../global/text/text';
import { AccountAdminSettingFormFooter } from '../common/account-admin-setting-form-footer';
import PassportAttendeeSSOFormVM from './oe-passport-attendee-sso-form.viewmodel';
import { ATTENDEE_SSO_STATUS_TEXT, AVAILABLE_CONFIGURATIONS_TEXT, ENFORCE_ON_ALL_EVENTS_TEXT } from './oe-passport-attendee-sso.text';
import { FormattedMessage } from 'react-intl';
import { LOADING } from '../../../constants/common-labels.constants';
import './oe-passport-attendee-sso.scss';

interface IOEPassportAttendeeSSOPresentationProps {
    vm: PassportAttendeeSSOFormVM;
}

interface ProviderItemProps {
    id: string;
    name: string;
    enabled: boolean;
    domains: string[];
    tempValue?: string;
    validationError?: string;
    onToggle: () => void;
    onChange: (value: string) => void;
    onBlur: (value: string) => void;
    enforcedOnAllEvents: boolean;
    onToggleEnforcedOnAllEvents: () => void;
}

const ProviderItem: React.FC<ProviderItemProps> = ({
    id,
    name,
    enabled,
    domains,
    tempValue,
    validationError,
    onToggle,
    onChange,
    onBlur,
    enforcedOnAllEvents,
    onToggleEnforcedOnAllEvents,
}) => (
    <div key={id} className='passport_attendee_sso__items-section__item-container'>
        <div className='passport_attendee_sso__items-section__item-container__item-wrapper'>
            <OEToggle
                isSelected={enabled}
                onClick={onToggle}
                dataTestId={`toggle-provider-${id}`}
            />
            <Text
                className='passport_attendee_sso__items-section__item-label'
                message={name}
                withEllipsis
            />
        </div>

        {enabled && (
            <div className='passport_attendee_sso__items-section__item-container__input-wrapper'>
                <input
                    className='oe-form-field__control'
                    type='text'
                    value={tempValue ?? domains.join(',')}
                    onChange={e => onChange(e.target.value)}
                    onBlur={e => onBlur(e.target.value)}
                />
                {validationError && (
                    <span className='oe-form-field__error word-wrap'>
                        {validationError}
                    </span>
                )}
                <div className='oe-form-field__title'>Please enter a comma-separated list of email domains.</div>
            </div>
        )}
        {enabled && <div className='passport_attendee_sso__items-section__item-container__item-wrapper m-10px'>
            <OEToggle
                isSelected={enforcedOnAllEvents}
                onClick={onToggleEnforcedOnAllEvents}
                dataTestId={`toggle-provider-${id}`}
            />
            <Text
                className='passport_attendee_sso__items-section__item-label'
                {...ENFORCE_ON_ALL_EVENTS_TEXT}
                withEllipsis
                values={{prefix: enforcedOnAllEvents ? 'Enforced' : 'Enforce'}}
            />
        </div>}
    </div>
);

const OEPassportAttendeeSSOFormPresentation = observer((props: IOEPassportAttendeeSSOPresentationProps): JSX.Element => {
    const { vm } = props;

    return (
        <CommonPopupBodyFooterLayout
            body={
                <div className='oe-form'>
                    {vm.isLoading ? (
                        <FormattedMessage
                            {...LOADING}
                            values={{
                                suffix: '...',
                            }}
                        />
                    ) : (
                        <div className='oe-form__body'>
                            <div className='m-top-10px m-bottom-0px flex center'>
                                <OEToggle
                                    isSelected={vm.settingsEntity.enabled}
                                    onClick={vm.settingsEntity.toggleEnabled}
                                    dataTestId='oe-passport-attendee-sso'
                                />
                                <Text
                                    {...ATTENDEE_SSO_STATUS_TEXT}
                                    values={{
                                        suffix: vm.settingsEntity.suffix
                                    }}
                                    className='line-height-25px'
                                />
                            </div>
                            {vm.settingsEntity.enabled &&
                                <div className='m-top-10px m-bottom-0px'>
                                    <div className='passport_attendee_sso__items-section'>
                                        <div className='passport_attendee_sso__items-section__title'>
                                            <FormattedMessage {...AVAILABLE_CONFIGURATIONS_TEXT} />
                                        </div>
                                        <div className='passport_attendee_sso__items-section__selected-items'>
                                            {vm.allProviders.length > 0 && vm.allProviders.map(provider => {
                                                return (<ProviderItem
                                                    key={provider.fusionAuthIdpId}
                                                    id={provider.fusionAuthIdpId}
                                                    name={provider.fusionAuthIdpName}
                                                    enabled={provider.enabled}
                                                    domains={provider.managedDomains}
                                                    tempValue={vm.tempInputValue[provider.fusionAuthIdpId]}
                                                    validationError={vm.domainValidationErrors[provider.fusionAuthIdpId]}
                                                    onToggle={() => vm.handleProviderToggleChange(provider.fusionAuthIdpId)}
                                                    onChange={value => vm.handleProviderDomainsChange(provider.fusionAuthIdpId, value)}
                                                    onBlur={value => vm.validateProviderDomains(provider.fusionAuthIdpId, value)}
                                                    enforcedOnAllEvents={provider.enabledForAllEvents}
                                                    onToggleEnforcedOnAllEvents={() =>
                                                        vm.handleProviderEnforcedOnAllEventsToggleChange(provider.fusionAuthIdpId)
                                                    }
                                                />);
                                            })}
                                        </div>
                                        {vm.settingsEntity.enabled && vm.getIdentityProviderEnabledValidationError() && vm.formSubmitted && (
                                            <div className='error-text m-top-10px'>
                                                <Text
                                                    message={vm.getIdentityProviderEnabledValidationError()}
                                                    className='text--error'
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }
                        </div>
                    )}
                </div>
            }
            footer={
                <AccountAdminSettingFormFooter
                    handleClose={vm.handleClose}
                    handleSubmit={vm.handleSubmit}
                    isDisabled={vm.isDisabled()}
                    hideApplyAndCloseButton={true}
                    closeWhenClickApplyButton={true}
                />
            }
        />
    );
});

export default OEPassportAttendeeSSOFormPresentation;
